import { useParams } from 'react-router';
import SimpleCheckbox from 'ui/checkbox';
import { FormValues } from '../../lib/hooks/useFacility2PageForm';
import { useChangeObjectActivityStateMutation } from '../../api/mutations/generated/ChangeObjectActivityState';
import { useObject } from 'utils/api/useObject';
import { updateObjectCache } from '../../api/cache/updateObjectCache';
import clsx from 'clsx';
import Modal from 'ui/modal';
import { useState } from 'react';

interface IFacilityAccess {
  values: FormValues;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

const FacilityAccess = ({ setFieldValue, values }: IFacilityAccess) => {
  const { facilityId } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { object } = useObject();

  const [changeActivityState] = useChangeObjectActivityStateMutation({
    update: (cache, { data }) => {
      if (data?.changeObjectActivityState !== undefined && facilityId) {
        updateObjectCache(cache, facilityId, data.changeObjectActivityState);
      }
    },
  });

  const handleActivityStateChange = async (skipModal = false) => {
    if (!facilityId || !object) return;

    const newIsActive = !object.isActive;

    if (object.isActive && !skipModal) {
      setIsModalOpen(true);
      return;
    }

    try {
      await changeActivityState({
        variables: {
          input: {
            objectId: facilityId,
            isActive: newIsActive,
          },
        },
      });
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error changing activity state:', error);
    }
  };

  const handleModalConfirm = async () => {
    await handleActivityStateChange(true);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(e.target.name, e.target.checked);
  };

  const isTimezoneSelected = Boolean(values.timezone);
  const isTimesheetInherited = values.isTimesheetInherited;

  return (
    <div className="col-span-full xl:col-span-3 md:col-span-5">
      <div className="px-6 h-fit pb-5 border rounded-lg shadow-smena bg-smena_white">
        <span className="Subtitle1 text-smena_text-secondary py-5 inline-block col-span-full">Настройки</span>
        <div className="grid gap-y-3">
          <div className="flex flex-col gap-y-1">
            <SimpleCheckbox
              divClassName="row-span-1 flex items-center mb-2"
              label="Отображать табель в этом объекте"
              onChange={handleCheckboxChange}
              checked={values.showTimesheet}
              name="showTimesheet"
              disabled={!isTimezoneSelected || isTimesheetInherited || !!facilityId}
            />
            {!facilityId && !isTimezoneSelected && !isTimesheetInherited && (
              <span className="Body1 text-smena_text-secondary">
                Чтобы отображать табель в этом объекте, необходимо выбрать часовой пояс.
              </span>
            )}
            {!facilityId && isTimesheetInherited && (
              <span className="Body1 text-smena_text-secondary">
                Табель уже отображается в родительском объекте или его предках. Вы не можете изменить эту настройку.
              </span>
            )}
          </div>
          <hr className="mb-3" />
          <SimpleCheckbox
            divClassName="row-span-1 flex items-center"
            label="Отображать исполнителям выплаты"
            onChange={handleCheckboxChange}
            checked={values.isFinanceActive}
            name="isFinanceActive"
          />
          <SimpleCheckbox
            divClassName="row-span-1 flex items-center"
            label="Отображать доход до модерации"
            onChange={handleCheckboxChange}
            checked={values.showSalaryBeforeModeration}
            name="showSalaryBeforeModeration"
          />
          <SimpleCheckbox
            divClassName="row-span-1 flex items-center"
            label="Отображать исполнителям бонусы"
            onChange={handleCheckboxChange}
            checked={values.isBonusActive}
            name="isBonusActive"
          />
          <SimpleCheckbox
            divClassName="row-span-1 flex items-center"
            label="По умолчанию разрешать исполнителям работу без Наймикс"
            onChange={handleCheckboxChange}
            checked={values.allowWorkWithoutNaimix}
            name="allowWorkWithoutNaimix"
          />
        </div>

        {facilityId && (
          <>
            <hr className="mt-6" />
            <button
              className={clsx('my-4', {
                'btn-reject': object?.isActive,
                'btn-primary': !object?.isActive,
              })}
              type="button"
              onClick={() => handleActivityStateChange()}
            >
              {object?.isActive ? 'Деактивировать объект' : 'Активировать объект'}
            </button>
            {object?.isActive && (
              <div className="Body1 text-smena_text-secondary">
                При деактивации объекта все внутренние объекты (если они есть) также будут деактивированы, а все
                исполнители будут откреплены.
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        title="Деактивация объекта"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleModalConfirm}
        okText="Подтвердить"
        type="delete"
      >
        Вы уверены, что хотите деактивировать объект?
      </Modal>
    </div>
  );
};

export default FacilityAccess;
